import { useEffect, useRef, useState } from 'react';
import { Editor, isTextSelection } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEditorState } from '@tiptap/react';
import { useReduxState } from 'hooks';
import { Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import { TextInput } from 'components/common/form/common';

interface Props {
  colorPickerColors?: string[];
  editor: Editor;
  richTextElementRef: React.RefObject<HTMLDivElement>;
}

const RichTextEditorMenu = ({
  colorPickerColors = [],
  editor,
  richTextElementRef
}: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const colorPickerEl = useRef<HTMLDivElement>(undefined);
  const linkEditorEl = useRef<HTMLDivElement>(undefined);

  const [colorPickerActive, setColorPickerActive] = useState(false);
  const [isBubbleMenuMounted, setIsBubbleMenuMounted] = useState(false);
  const [linkEditorActive, setLinkEditorActive] = useState(false);
  const [linkEditorValue, setLinkEditorValue] = useState('');

  const activeState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor.isActive('bold'),
      isCode: editor.isActive('code'),
      isItalic: editor.isActive('italic'),
      isLink: editor.isActive('link'),
      isTextStyle: editor.isActive('text-style'),
      isUnderline: editor.isActive('underline')
    })
  });

  const getLinkProps = (value: string) => ({
    href: value,
    target:
      value?.startsWith(serverConfig.adminUrl) ||
      value?.startsWith('mailto:') ||
      value?.startsWith('tel:')
        ? null
        : '_blank'
  });

  const isMac = /Mac/.test(window?.navigator.userAgent);
  const modifierKey = isMac ? 'cmd' : 'ctrl';

  // BubbleMenu registers a window resize listener on mount. Doing that as soon as a chapter renders
  // can run coordsAtPos while the view is still settling and ProseMirror’s docView is null.
  // Delay mounting until the first focus to avoid runtime errors.
  useEffect(() => {
    if (editor.isDestroyed) return;

    const mountBubbleMenu = () => setIsBubbleMenuMounted(true);

    if (editor.isFocused) mountBubbleMenu();
    editor.on('focus', mountBubbleMenu);

    return () => {
      editor.off('focus', mountBubbleMenu);
      setIsBubbleMenuMounted(false);
    };
  }, [editor]);

  if (!editor || !isBubbleMenuMounted) return null;

  return (
    <BubbleMenu
      className="vuo-rich-text-menu"
      editor={editor}
      pluginKey="menu-format"
      shouldShow={({ view, state, from, to }) => {
        if (!editor.isInitialized) return false;

        // in addition to the default display logic, we also want to display
        // the bubble menu if the cursor is placed within a link, so this
        // is a copy of the default shoulShow logic with the added check
        // if a link is active
        const { doc, selection } = state;
        const { empty } = selection;
        const isEmptyTextBlock =
          !doc.textBetween(from, to).length && isTextSelection(state.selection);
        const isChildOfMenu = richTextElementRef.current
          ?.querySelector('.vuo-rich-text-menu')
          ?.contains(document.activeElement);
        const hasEditorFocus = view.hasFocus() || isChildOfMenu;
        if (editor.isActive('link') && empty) {
          setLinkEditorActive(true);
          setLinkEditorValue(editor.getAttributes('link')?.href || '');
          return true;
        } else {
          setLinkEditorValue('');
          setLinkEditorActive(false);
        }
        if (
          !hasEditorFocus ||
          empty ||
          isEmptyTextBlock ||
          !editor.isEditable
        ) {
          return false;
        }
        return true;
      }}
      options={{
        offset: 10,
        placement: 'top',
        onHide: () => {
          if (!editor.isInitialized) return;
          setLinkEditorActive(false);
          setColorPickerActive(false);
        }
      }}
    >
      <div
        className="vuo-rich-text-menu-link-editor"
        ref={linkEditorEl}
        style={{ display: linkEditorActive ? 'flex' : 'none' }}
      >
        <TextInput
          onChange={(event) => setLinkEditorValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              editor
                .chain()
                .extendMarkRange('link')
                .setLink(getLinkProps(linkEditorValue))
                .run();
            }
          }}
          placeholder={messages.commonAddLink}
          style={{ padding: '5px 8px', width: 200 }}
          value={linkEditorValue}
        />

        <Tooltip content={messages.commonOpenLink}>
          <a
            className={`vuo-rich-text-menu-button ${
              linkEditorValue ? '' : 'vuo-rich-text-menu-button-disabled'
            }`}
            href={linkEditorValue ? linkEditorValue : undefined}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon icon="external-link-alt" />
          </a>
        </Tooltip>

        <Tooltip content={messages.commonRemoveLink}>
          <button
            onClick={() => {
              editor.chain().extendMarkRange('link').unsetLink().run();
            }}
            className="vuo-rich-text-menu-button"
            disabled={!linkEditorValue}
            type="button"
          >
            <Icon icon="trash-alt" />
          </button>
        </Tooltip>

        <Tooltip content={messages.commonSetLink}>
          <button
            onClick={() => {
              editor
                .chain()
                .extendMarkRange('link')
                .setLink(getLinkProps(linkEditorValue))
                .run();
            }}
            className="vuo-rich-text-menu-button"
            disabled={!linkEditorValue}
            type="button"
          >
            <Icon icon="check" />
          </button>
        </Tooltip>
      </div>

      <div
        className="vuo-rich-text-menu-color-picker"
        ref={colorPickerEl}
        style={{ display: colorPickerActive ? 'flex' : 'none' }}
      >
        {colorPickerColors?.length &&
          ['#333', ...colorPickerColors].map((color, i) => {
            const activeColor = editor.getAttributes('textStyle')?.color;
            const isActive = activeColor === color || (i === 0 && !activeColor);

            return (
              <button
                key={color}
                onClick={() => editor.chain().focus().setColor(color).run()}
                className="vuo-rich-text-menu-button"
                style={{
                  borderRadius: '50%',
                  backgroundColor: color,
                  opacity: 0.8
                }}
                type="button"
              >
                {isActive ? (
                  <Icon icon="check" style={{ color: 'white' }} />
                ) : null}
              </button>
            );
          })}
      </div>

      <div
        className="vuo-rich-text-menu-inner"
        style={{
          display: linkEditorActive || colorPickerActive ? 'none' : 'flex'
        }}
      >
        <Tooltip content={`${messages.commonBold} (${modifierKey}+b)`}>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`vuo-rich-text-menu-toggle ${
              activeState.isBold ? 'vuo-rich-text-menu-toggle-active' : ''
            }`}
            type="button"
          >
            <Icon icon="bold" />
          </button>
        </Tooltip>

        <Tooltip content={`${messages.commonItalic} (${modifierKey}+i)`}>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`vuo-rich-text-menu-toggle ${
              activeState.isItalic ? 'vuo-rich-text-menu-toggle-active' : ''
            }`}
            type="button"
          >
            <Icon icon="italic" />
          </button>
        </Tooltip>

        <Tooltip content={`${messages.commonUnderline} (${modifierKey}+u)`}>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`vuo-rich-text-menu-toggle ${
              activeState.isUnderline ? 'vuo-rich-text-menu-toggle-active' : ''
            }`}
            type="button"
          >
            <Icon icon="underline" />
          </button>
        </Tooltip>

        <Tooltip content={`${messages.blocksPre} (${modifierKey}+shift+c)`}>
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={`vuo-rich-text-menu-toggle ${
              activeState.isCode ? 'vuo-rich-text-menu-toggle-active' : ''
            }`}
            type="button"
          >
            <Icon icon="code" />
          </button>
        </Tooltip>

        <Tooltip content={messages.commonAddLink}>
          <button
            onClick={() => {
              setLinkEditorActive(true);
              setTimeout(() =>
                linkEditorEl.current?.querySelector('input')?.focus()
              );
            }}
            className={`vuo-rich-text-menu-toggle ${
              activeState.isLink ? 'vuo-rich-text-menu-toggle-active' : ''
            }`}
            type="button"
          >
            <Icon icon="link" />
          </button>
        </Tooltip>

        {colorPickerColors?.length ? (
          <button
            onClick={() => setColorPickerActive(true)}
            className={`vuo-rich-text-menu-toggle ${
              activeState.isTextStyle ? 'vuo-rich-text-menu-toggle-active' : ''
            }`}
            type="button"
          >
            <Icon icon="palette" />
          </button>
        ) : null}
      </div>

      <style jsx global>{`
        .vuo-rich-text-menu {
          background-color: white;
          border-radius: ${theme.dropdownBorderRadius};
          box-shadow: ${theme.dropdownShadow};
          padding: 3px;
          position: relative;
          z-index: 10;
        }
        .vuo-rich-text-menu-link-editor {
          align-items: center;
          display: flex;
          gap: 5px;
          margin-right: 2px;
        }
        .vuo-rich-text-menu-color-picker {
          align-items: center;
          display: flex;
          gap: 5px;
          margin-right: 2px;
          padding: 3px;
        }
        .vuo-rich-text-menu-inner {
          align-items: center;
          display: flex;
          gap: 2px;
        }
        .vuo-rich-text-menu-button,
        .vuo-rich-text-menu-toggle {
          align-items: center;
          border: 0;
          border-radius: 6px;
          box-shadow: 0;
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: inline-flex;
          justify-content: center;
          transition: background-color 0.3s, color 0.3s;
        }
        .vuo-rich-text-menu-button {
          background-color: ${theme.lightGrey};
          font-size: 1.2rem;
          width: 28px;
          height: 28px;
        }
        .vuo-rich-text-menu-button:hover {
          background-color: ${theme.lightGreyActive};
        }
        .vuo-rich-text-menu-button:disabled,
        .vuo-rich-text-menu-button-disabled {
          background-color: ${theme.lightGrey};
          cursor: default;
          opacity: 0.5;
        }
        .vuo-rich-text-menu-toggle {
          background-color: transparent;
          font-size: 1.4rem;
          width: 32px;
          height: 32px;
        }
        .vuo-rich-text-menu-toggle-active {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColor};
        }
        .vuo-rich-text-menu-toggle:hover {
          background-color: ${theme.lightGrey};
        }
      `}</style>
    </BubbleMenu>
  );
};

export default RichTextEditorMenu;
